import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhoneVolume,
  faDiagramProject,
  faPenNib,
  faRocket,
  faGaugeHigh,
  faArrowsRotate,
} from "@fortawesome/free-solid-svg-icons";
import "./Process.css";

const Process = () => {
  const steps = [
    {
      title: "Discovery & goals",
      description:
        "We start with a focused strategy call: where you are now, how you acquire customers today, and what “good” would look like in the next 3–6 months.",
      icon: faPhoneVolume,
      label: "Step 01",
      signal: "Clarity on targets, constraints & timelines",
    },
    {
      title: "Audit & growth map",
      description:
        "We review your analytics, ad accounts, funnels, SEO, and email setup to spot quick wins and structural gaps, then turn that into a simple growth map.",
      icon: faDiagramProject,
      label: "Step 02",
      signal: "One page that connects channels to revenue",
    },
    {
      title: "Plan your first sprint",
      description:
        "Together we pick the starting lane: ads, funnels, lifecycle, or tracking. We define a 4–6 week sprint with clear deliverables, owners, and success metrics.",
      icon: faPenNib,
      label: "Step 03",
      signal: "Small, shippable scope instead of a giant rebuild",
    },
    {
      title: "Build, wire & launch",
      description:
        "We design and ship the assets that matter: campaigns, pages, automations, and tracking. Everything is wired to the same source of truth for reporting.",
      icon: faRocket,
      label: "Step 04",
      signal: "Live changes in weeks, not quarters",
    },
    {
      title: "Measure & optimise",
      description:
        "Once live, we track how real users behave, then iterate: new angles, creative, audiences, and on-page tweaks based on data—not guesses.",
      icon: faGaugeHigh,
      label: "Step 05",
      signal: "Regular reviews, clear learnings & next tests",
    },
    {
      title: "Scale & repeat",
      description:
        "When the first lane is working, we expand into adjacent channels or deeper lifecycle work, always keeping one shared backlog and roadmap.",
      icon: faArrowsRotate,
      label: "Step 06",
      signal: "A compounding system, not one-off hacks",
    },
  ];

  return (
    <section className="proc2" aria-label="How we run projects together">
      <div className="proc2-wrap">
        {/* Header */}
        <header className="proc2-head">
          <p className="proc2-kicker">How we work with you</p>
          <h2>From scattered campaigns to one joined-up growth engine.</h2>
          <p className="proc2-sub">
            Whether you start with ads, funnels, SEO, email, or analytics, we
            plug everything into one process: understand, map, ship, learn, and
            scale. No mystery “black box” work—just a clear, repeatable rhythm.
          </p>
        </header>

        {/* Main layout: steps + side note */}
        <div className="proc2-layout">
          {/* Steps rail + cards */}
          <div className="proc2-flow">
            <div className="proc2-rail" aria-hidden="true" />
            <ol className="proc2-grid">
              {steps.map((s, i) => (
                <li className="proc2-card" key={s.title}>
                  <div className="proc2-dot" aria-hidden="true" />
                  <div className="proc2-meta-row">
                    <div className="proc2-step-pill">
                      <span className="proc2-step-index">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="proc2-step-label">{s.label}</span>
                    </div>
                    <div className="proc2-icon">
                      <FontAwesomeIcon icon={s.icon} />
                    </div>
                  </div>

                  <h3>{s.title}</h3>
                  <p>{s.description}</p>

                  <div className="proc2-signal">
                    <span className="dot" aria-hidden="true" />
                    <span>{s.signal}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Side card */}
          <aside className="proc2-aside" aria-label="What we need from you">
            <div className="proc2-aside-card">
              <p className="proc2-aside-kicker">What we need from your side</p>
              <h3>A small, responsive team on your end.</h3>
              <p>
                Great work doesn’t need endless meetings—but it does need a
                clear owner. We keep things light, structured, and easy for your
                team to plug into.
              </p>

              <ul className="proc2-aside-list">
                <li>Access to analytics, ad accounts, and core tools</li>
                <li>1–2 decision makers for fast feedback</li>
                <li>Time-boxed reviews every 1–2 weeks</li>
                <li>Honest input on what’s realistic internally</li>
              </ul>

              <div className="proc2-aside-foot">
                <span className="pill">Asynchronous updates</span>
                <span className="pill">Looms & written recaps</span>
                <span className="pill">No bloated status calls</span>
              </div>
            </div>
          </aside>
        </div>

        {/* Micro KPIs */}
        <div className="proc2-stats" role="list">
          <div className="stat" role="listitem">
            <div className="k">7–21 days</div>
            <div className="d">to first live changes after kick-off</div>
          </div>
          <div className="stat" role="listitem">
            <div className="k">20–60%</div>
            <div className="d">
              typical uplift in key conversions within early sprints
            </div>
          </div>
          <div className="stat" role="listitem">
            <div className="k">1 team</div>
            <div className="d">owning ads, funnels, email & tracking</div>
          </div>
        </div>

        {/* CTA */}
        <div className="proc2-cta">
          <a
            className="btn btn-primary"
            href="/contact"
            aria-label="Schedule a project discovery call"
          >
            Schedule a discovery call
          </a>
          <a
            className="btn btn-ghost"
            href="/services"
            aria-label="View all services and engagement options"
          >
            See services & engagement options
          </a>
        </div>
      </div>
    </section>
  );
};

export default Process;
